import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';

import { select, Store } from '@ngrx/store';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import {
  ActivitiesDataScope,
  DefaultActivitiesAndNotificationsFilters,
  NotificationsActions,
  NotificationsSelectors,
} from '@recruitee/activities-and-notifications-data';
import type { TrackingActivity } from '@recruitee/activities-types';
import { filterBoolean, trackByProp } from '@recruitee/common';
import { TranslatePipe } from '@recruitee/i18n';
import {
  ButtonComponent,
  EmptyStateComponent,
  SpinnerComponent,
  DropdownItemComponent,
} from '@recruitee/user-interface';

import { ActivityRedirectService } from '../../services/activity-redirect.service';
import { NotificationReadUnreadService } from '../../services/notification-read-unread.service';
import { ActivityItemComponent } from '../../ui/activities-list/activity-item/activity-item.component';

type NotificationsPaginationState = {
  page: number;
  limit: number;
  cursorPosition: number | null;
  cursorOrder: string;
};

@Component({
  selector: 'rt-notification-list-container',
  templateUrl: './notification-list-container.component.html',
  styleUrls: ['./notification-list-container.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    InfiniteScrollModule,
    DropdownItemComponent,
    ActivityItemComponent,
    SpinnerComponent,
    EmptyStateComponent,
    ButtonComponent,
    TranslatePipe,
  ],
})
export class NotificationListContainerComponent implements OnInit {
  @Input() public scope: ActivitiesDataScope.DropdownUnread | ActivitiesDataScope.Dropdown;
  @Input() public containerRef: HTMLElement;

  @Output() public itemClick: EventEmitter<TrackingActivity> = new EventEmitter();

  public canManageCandidates = false;
  public notifications: TrackingActivity[] = [];
  public unreadCount: number = 0;

  public loaded: boolean = false;
  public loading: boolean = false;

  public data$: Observable<TrackingActivity[]>;
  public dataUnread$: Observable<TrackingActivity[]>;
  public isPending$: Observable<boolean>;
  public isLoaded$: Observable<boolean>;
  public infiniteScrollDistance: number = 2;

  public trackByActivity = trackByProp<TrackingActivity>({ prop: 'id' });

  private ngOnDestroy$ = new Subject<void>();

  private canFetchCollection: boolean = true;

  private paginationState$: BehaviorSubject<NotificationsPaginationState> =
    new BehaviorSubject<NotificationsPaginationState>({
      limit: 20,
      page: 1,
      cursorPosition: null,
      cursorOrder: 'desc',
    });

  constructor(
    private store: Store,
    protected cdRef: ChangeDetectorRef,
    private activityRedirectService: ActivityRedirectService,
    private notificationReadUnreadService: NotificationReadUnreadService,
  ) {}

  // eslint-disable-next-line max-lines-per-function
  public ngOnInit(): void {
    this.checkAbility();

    this.data$ = this.store.select(NotificationsSelectors.entities({ scope: this.scope }));

    this.isLoaded$ = this.store.select(NotificationsSelectors.isLoaded({ scope: this.scope }));

    this.isPending$ = this.store.select(NotificationsSelectors.isPending({ scope: this.scope }));

    this.data$
      .pipe(
        takeUntil(this.ngOnDestroy$.asObservable()),
        filterBoolean,
        filter(data => !!data.length),
      )
      .subscribe(notifications => {
        this.notifications = notifications;
        this.cdRef.detectChanges();
      });

    this.isLoaded$.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(loaded => {
      this.loaded = loaded;
      this.cdRef.detectChanges();
    });

    this.isPending$.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(loading => {
      this.loading = loading;
      this.cdRef.detectChanges();
    });

    this.paginationState$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(paginationState => {
      this.fetchData(paginationState);
    });

    combineLatest([this.data$, this.paginationState$]).pipe(
      takeUntil(this.ngOnDestroy$),
      first(),
      map(([activities, paginationState]) => {
        this.canFetchCollection = activities?.length === paginationState.limit;
      }),
    );
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onMarkReadUnread(event: { read: boolean; id: number }) {
    this.notificationReadUnreadService.markNotificationAsReadUnread(event.read, event.id);
  }

  public onActivityItemClick(activity: TrackingActivity): void {
    this.activityRedirectService.redirect(activity);
    this.itemClick.emit(activity);
  }

  public loadMore(): void {
    const paginationState = this.paginationState$.getValue();
    const { limit, page: currentPage } = paginationState;
    if (currentPage * limit > this.notifications.length) return;

    const page = (currentPage || DefaultActivitiesAndNotificationsFilters.page) + 1;
    this.infiniteScrollDistance = 2 / currentPage;
    this.paginationState$.next({
      ...paginationState,
      page,
      cursorPosition: this.notifications[this.notifications.length - 1].id,
    });
  }

  private fetchData(paginationState: NotificationsPaginationState) {
    if (!this.canFetchCollection) return;

    this.store.dispatch(
      NotificationsActions.fetchCollection({
        payload: {
          params: { ...DefaultActivitiesAndNotificationsFilters, ...paginationState },
          scope: this.scope,
          replace: paginationState.page === 1,
        },
      }),
    );
  }

  private checkAbility(): void {
    this.store
      .pipe(select(AbilitiesSelectors.abilityMap), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(enabledMap => {
        this.canManageCandidates = enabledMap[Ability.ManageCandidates];
      });
  }
}
